import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
  IconButton,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  SkipPrevious,
  SkipNext,
  Speed,
  Fullscreen,
  FullscreenExit
} from '@mui/icons-material';
import { 
  createAnimationFrames,
  type AnimationFrame,
  type DateRange 
} from '../services/geeService';

interface GEEAnimationProps {
  open: boolean;
  onClose: () => void;
  collectionId: string;
  dateRange: DateRange;
  bounds?: {
    west: number;
    south: number;
    east: number;
    north: number;
  };
}

const GEEAnimation: React.FC<GEEAnimationProps> = ({
  open,
  onClose,
  collectionId,
  dateRange,
  bounds
}) => {
  const [frames, setFrames] = useState<AnimationFrame[]>([]);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [speed, setSpeed] = useState(1); // frames per second
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [frameUrls, setFrameUrls] = useState<string[]>([]);
  
  const animationRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load animation frames
  useEffect(() => {
    if (open && collectionId && dateRange) {
      loadAnimationFrames();
    }
  }, [open, collectionId, dateRange]);

  // Handle animation playback
  useEffect(() => {
    if (isPlaying && frames.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentFrame(prev => {
          if (prev >= frames.length - 1) {
            setIsPlaying(false);
            return 0;
          }
          return prev + 1;
        });
      }, 1000 / speed);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, frames.length, speed]);

  const loadAnimationFrames = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await createAnimationFrames(
        collectionId,
        dateRange.startDate,
        dateRange.endDate,
        bounds,
        20 // frameCount
      );
      
      setFrames(result.frames);
      setCurrentFrame(0);
      
      // Use backend-provided thumbnail URLs when available
      const urls = result.frames.map((frame) => frame.thumbnailUrl || '');
      setFrameUrls(urls);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load animation frames');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlay = () => {
    if (frames.length > 0) {
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleStop = () => {
    setIsPlaying(false);
    setCurrentFrame(0);
  };

  const handlePrevious = () => {
    setCurrentFrame(prev => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentFrame(prev => Math.min(frames.length - 1, prev + 1));
  };

  const handleFrameChange = (event: Event, newValue: number | number[]) => {
    setCurrentFrame(newValue as number);
  };

  const handleSpeedChange = (event: Event, newValue: number | number[]) => {
    setSpeed(newValue as number);
  };

  const toggleFullscreen = () => {
    if (animationRef.current) {
      if (!isFullscreen) {
        animationRef.current.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
      setIsFullscreen(!isFullscreen);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getProgressPercentage = () => {
    return frames.length > 0 ? (currentFrame / (frames.length - 1)) * 100 : 0;
  };

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Loading Animation...</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          height: isFullscreen ? '100vh' : '80vh',
          maxHeight: isFullscreen ? '100vh' : '80vh'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Time-lapse Animation: {collectionId.replace('-', ' ').toUpperCase()}
          </Typography>
          <IconButton onClick={toggleFullscreen}>
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {formatDate(dateRange.startDate)} - {formatDate(dateRange.endDate)}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box ref={animationRef} sx={{ position: 'relative', height: '100%' }}>
          {/* Animation Display */}
          <Paper 
            elevation={3} 
            sx={{ 
              height: '60%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: 'black',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {frames.length > 0 && frameUrls[currentFrame] ? (
              <Box
                component="img"
                src={frameUrls[currentFrame]}
                alt={`Frame ${currentFrame + 1}`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            ) : (
              <Typography color="white">
                No frames available
              </Typography>
            )}
            
            {/* Frame Info Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                bgcolor: 'rgba(0,0,0,0.7)',
                color: 'white',
                p: 1,
                borderRadius: 1
              }}
            >
              <Typography variant="caption">
                Frame {currentFrame + 1} of {frames.length}
              </Typography>
              {frames[currentFrame] && (
                <Typography variant="caption" display="block">
                  {formatDate(frames[currentFrame].date)}
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Controls */}
          <Box sx={{ mt: 2 }}>
            {/* Frame Slider */}
            <Box sx={{ mb: 2 }}>
              <Slider
                value={currentFrame}
                onChange={handleFrameChange}
                min={0}
                max={Math.max(0, frames.length - 1)}
                step={1}
                marks
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value + 1}/${frames.length}`}
              />
            </Box>

            {/* Control Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <IconButton onClick={handlePrevious} disabled={currentFrame === 0}>
                <SkipPrevious />
              </IconButton>
              
              <IconButton 
                onClick={isPlaying ? handlePause : handlePlay}
                disabled={frames.length === 0}
                sx={{ bgcolor: 'primary.main', color: 'white' }}
              >
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>
              
              <IconButton onClick={handleStop} disabled={frames.length === 0}>
                <Stop />
              </IconButton>
              
              <IconButton onClick={handleNext} disabled={currentFrame === frames.length - 1}>
                <SkipNext />
              </IconButton>

              <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Speed fontSize="small" />
                <Slider
                  value={speed}
                  onChange={handleSpeedChange}
                  min={0.25}
                  max={4}
                  step={0.25}
                  sx={{ width: 100 }}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}x`}
                />
              </Box>
            </Box>

            {/* Progress and Info */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip 
                label={`${getProgressPercentage().toFixed(1)}%`} 
                size="small" 
                variant="outlined" 
              />
              <Typography variant="body2" color="text.secondary">
                {frames.length} frames • {speed.toFixed(2)}x speed
              </Typography>
            </Stack>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GEEAnimation; 